import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getGroupQuizData,
  GroupQuizData,
  SectionContent,
  saveQuizResponse,
  markContentAsCompleted
} from '../../Services/UserProjectService';
import { useHeaderVisibility } from '../../Context/HeaderVisibilityContext';
import { useFeedback } from '../../Context/FeedbackContext';
import ContentViewer from '../../Components/User/ContentViewer';
import ProgressBar from '../../Components/User/ProgressBar';

const ProjectQuiz: React.FC = () => {
  const { projectId, groupId, sectionIndex: sectionIndexParam } = useParams<{ projectId: string; groupId: string; sectionIndex?: string }>();
  const navigate = useNavigate();
  const { setAutoHideActive, setHeaderVisible } = useHeaderVisibility();
  const { setProjectSectionContentId } = useFeedback();
  
  
  const [groupData, setGroupData] = useState<GroupQuizData | null>(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentContentIndex, setCurrentContentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [navigationDirection, setNavigationDirection] = useState<'forward' | 'backward' | undefined>(undefined);
  const [isClockHovered, setIsClockHovered] = useState(false);

  // Response management state
  const [currentResponse, setCurrentResponse] = useState<string>('');
  const [currentResponseType, setCurrentResponseType] = useState<string>('');
  const [isResponseValid, setIsResponseValid] = useState(false);
  const [isSavingResponse, setIsSavingResponse] = useState(false);

  // Hide main header immediately when component mounts (quiz mode)
  useEffect(() => {
    setAutoHideActive(true);
    
    // Show main header when component unmounts
    return () => {
      setAutoHideActive(false);
    };
  }, [setAutoHideActive]);

  // Control header visibility based on clock hover
  useEffect(() => {
    setHeaderVisible(isClockHovered);
  }, [isClockHovered, setHeaderVisible]);

  useEffect(() => {
    if (!groupId || groupId === 'undefined') {
      setAutoHideActive(false);
      navigate('/user/dashboard');
      return;
    }
    
    const fetchGroupData = async () => {
      try {
        setLoading(true);
        const groupQuizData = await getGroupQuizData(groupId);
        setGroupData(groupQuizData);
        
        // Set first incomplete content as current, or handle completion scenario
        if (groupQuizData?.group?.sections && groupQuizData.group.sections.length > 0) {
          // If sectionIndex is provided via URL, navigate directly to that section
          if (sectionIndexParam !== undefined) {
            const targetSection = parseInt(sectionIndexParam, 10);
            if (targetSection >= 0 && targetSection < groupQuizData.group.sections.length) {
              console.log(`🎯 Navigating directly to section ${targetSection} from URL parameter`);

              // Find first incomplete content in this section
              const section = groupQuizData.group.sections[targetSection];
              let targetContentIndex = 0;

              if (section.contents && section.contents.length > 0) {
                for (let i = 0; i < section.contents.length; i++) {
                  if (!section.contents[i].isCompleted) {
                    targetContentIndex = i;
                    console.log(`📍 Found first incomplete content at index ${i}`);
                    break;
                  }
                  // If all completed, stay at last content
                  if (i === section.contents.length - 1) {
                    targetContentIndex = i;
                  }
                }
              }

              setCurrentSectionIndex(targetSection);
              setCurrentContentIndex(targetContentIndex);
              return;
            }
          }

          // Find first incomplete content manually using received data
          let targetSectionIndex = 0;
          let targetContentIndex = 0;
          let found = false;
          let totalContent = 0;
          let completedContent = 0;

          console.log('🔍 Searching for first incomplete content...');

          for (let sectionIndex = 0; sectionIndex < groupQuizData.group.sections.length; sectionIndex++) {
            const section = groupQuizData.group.sections[sectionIndex];
            console.log(`📂 Section ${sectionIndex}: ${section.name || 'Unnamed'} (${section.contents?.length || 0} contents)`);

            if (section.contents && section.contents.length > 0) {
              for (let contentIndex = 0; contentIndex < section.contents.length; contentIndex++) {
                const content = section.contents[contentIndex];
                totalContent++;

                console.log(`📄 Content ${contentIndex}: ${(content as any).contentType || 'Unknown'} - Completed: ${content.isCompleted}`);

                if (content.isCompleted) {
                  completedContent++;
                } else if (!found) {
                  // First incomplete content found
                  targetSectionIndex = sectionIndex;
                  targetContentIndex = contentIndex;
                  found = true;
                  console.log(`✅ First incomplete content found at Section ${sectionIndex}, Content ${contentIndex}`);
                }
              }
            }
          }

          console.log(`📊 Progress: ${completedContent}/${totalContent} content completed`);

          // Check if all content is completed
          if (!found && totalContent > 0) {
            console.log('🎉 All content completed! Redirecting to dashboard...');
            setAutoHideActive(false);
            navigate('/user/dashboard');
            return;
          }

          // If no content found at all, start from beginning
          if (totalContent === 0) {
            console.log('⚠️ No content found in any section, staying at 0,0');
          }

          console.log(`🎯 Navigating to Section ${targetSectionIndex}, Content ${targetContentIndex}`);
          setCurrentSectionIndex(targetSectionIndex);
          setCurrentContentIndex(targetContentIndex);
        } else {
          console.log('⚠️ No sections found in group data');
        }
      } catch (err) {
        setError('Error al cargar los datos del grupo');
      } finally {
        setLoading(false);
      }
    };

    fetchGroupData();
  }, [groupId, navigate, setAutoHideActive, sectionIndexParam]);



  // Get current content based on current section and content indices
  const getCurrentContent = (): SectionContent | null => {
    if (!groupData?.group?.sections || !groupData.group.sections[currentSectionIndex]) return null;
    const currentSection = groupData.group.sections[currentSectionIndex];
    return currentSection.contents?.[currentContentIndex] || null;
  };

  // Ref to track current indices for auto-advance
  const indicesRef = useRef({ sectionIndex: currentSectionIndex, contentIndex: currentContentIndex });
  useEffect(() => {
    indicesRef.current = { sectionIndex: currentSectionIndex, contentIndex: currentContentIndex };
  }, [currentSectionIndex, currentContentIndex]);

  // Auto-advance to next content
  const autoAdvanceToNext = useCallback(() => {
    if (!groupData?.group?.sections) return;

    const { sectionIndex, contentIndex } = indicesRef.current;
    const currentSection = groupData.group.sections[sectionIndex];

    setNavigationDirection('forward');

    if (currentSection.contents && contentIndex < currentSection.contents.length - 1) {
      setCurrentContentIndex(contentIndex + 1);
    } else {
      // Section completed - return to dashboard (section list)
      setAutoHideActive(false);
      navigate('/user/dashboard');
    }
  }, [groupData, navigate, setAutoHideActive]);

  // Handle response change from question components
  const handleResponseChange = useCallback((isValid: boolean, response: string, responseType: string) => {
    setIsResponseValid(isValid);
    setCurrentResponse(response);
    setCurrentResponseType(responseType);

    // Auto-advance if challenge signals completion
    if (isValid && response) {
      try {
        const parsed = JSON.parse(response);
        if (parsed.completed === true) {
          console.log('🚀 Challenge completed, auto-advancing...');
          setTimeout(() => {
            autoAdvanceToNext();
          }, 100);
        }
      } catch (e) {
        // Not JSON, ignore
      }
    }
  }, [autoAdvanceToNext]);

  // Reset response state when content changes
  useEffect(() => {
    setIsResponseValid(false);
    setCurrentResponse('');
    setCurrentResponseType('');
  }, [currentSectionIndex, currentContentIndex]);

  // Update feedback context with current content ID
  useEffect(() => {
    const currentContent = groupData?.group?.sections?.[currentSectionIndex]?.contents?.[currentContentIndex];
    if (currentContent?.id) {
      setProjectSectionContentId(currentContent.id);
    }

    // Clean up when unmounting
    return () => {
      setProjectSectionContentId(null);
    };
  }, [currentSectionIndex, currentContentIndex, groupData, setProjectSectionContentId]);

  // Helper function to mark content as completed and update local state
  const markCurrentContentAsCompleted = async (projectSectionContentId: string) => {
    await markContentAsCompleted(projectSectionContentId);

    // Update local state to reflect completion
    setGroupData(prevData => {
      if (!prevData || !prevData.group.sections) return prevData;

      const updatedSections = [...prevData.group.sections];
      if (updatedSections[currentSectionIndex] && updatedSections[currentSectionIndex].contents) {
        const updatedContents = [...updatedSections[currentSectionIndex].contents];
        if (updatedContents[currentContentIndex]) {
          updatedContents[currentContentIndex] = {
            ...updatedContents[currentContentIndex],
            isCompleted: true
          };
          updatedSections[currentSectionIndex] = {
            ...updatedSections[currentSectionIndex],
            contents: updatedContents
          };
        }
      }

      return {
        ...prevData,
        group: {
          ...prevData.group,
          sections: updatedSections
        }
      };
    });
  };

  const handleNext = async () => {
    if (!groupData?.group?.sections || isSavingResponse) return;

    const currentContent = getCurrentContent();
    const contentType = (currentContent as any)?.contentType;
    const isQuestionContent = currentContent &&
      ['QUESTION_SIMPLE', 'QUESTION_MULTIPLE', 'IMAGE_COMMENT'].includes(contentType);
    const isTextContent = currentContent &&
      ['TEXT_SIMPLE', 'TEXT_SELECTABLE'].includes(contentType);

    setIsSavingResponse(true);

    try {
      const projectSectionContentId = currentContent?.id;

      if (!projectSectionContentId) {
        console.error('No content ID found:', currentContent);
        setIsSavingResponse(false);
        return;
      }

      // Save response if it's a question and has valid response
      if (isQuestionContent && isResponseValid && currentResponse) {
        // Debug logging
        console.log('Saving response - projectId:', projectId);
        console.log('Saving response - projectSectionContentId:', projectSectionContentId);
        console.log('Saving response - content:', currentResponse);
        console.log('Saving response - type:', currentResponseType);

        // Save the response using correct endpoint
        await saveQuizResponse(
          projectId || '',
          projectSectionContentId,
          currentResponse,
          currentResponseType as any
        );
      }

      // Mark content as completed for both questions and text content
      if ((isQuestionContent && isResponseValid && currentResponse) || isTextContent) {
        await markCurrentContentAsCompleted(projectSectionContentId);
      }

    } catch (error) {
      console.error('Error processing content:', error);
      setIsSavingResponse(false);
      return; // Don't navigate if processing failed
    } finally {
      setIsSavingResponse(false);
    }

    setNavigationDirection('forward');

    // Get current section
    const currentSection = groupData.group.sections[currentSectionIndex];

    // Check if we can move to next content in current section
    if (currentSection.contents && currentContentIndex < currentSection.contents.length - 1) {
      setCurrentContentIndex(prev => prev + 1);
    } else {
      // Section completed - return to dashboard (section list)
      setAutoHideActive(false);
      navigate('/user/dashboard');
    }
  };

  const handlePrevious = () => {
    if (!groupData?.group?.sections) return;

    setNavigationDirection('backward');

    if (currentContentIndex > 0) {
      setCurrentContentIndex(prev => prev - 1);
    } else {
      // Move to previous section
      if (currentSectionIndex > 0) {
        const prevSectionIndex = currentSectionIndex - 1;
        const prevSection = groupData.group.sections[prevSectionIndex];
        setCurrentSectionIndex(prevSectionIndex);
        // Set to last content of previous section
        const lastContentIndex = prevSection.contents?.length ? prevSection.contents.length - 1 : 0;
        setCurrentContentIndex(lastContentIndex);
      }
    }
  };

  if (loading) {
    return (
      <div className="container-fluid py-5">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !groupData) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error</h4>
          <p>{error || 'Grupo no encontrado'}</p>
          <button 
            className="btn btn-outline-danger"
            onClick={() => {
              setAutoHideActive(false);
              navigate('/user/dashboard');
            }}
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }


  return (
    <>
      {/* Progress Bar - show when we have sections data */}
      {groupData?.group?.sections && (
        <ProgressBar
          sections={groupData.group.sections}
          currentSectionIndex={currentSectionIndex}
          currentContentIndex={currentContentIndex}
          navigationDirection={navigationDirection}
          onHoverChange={setIsClockHovered}
        />
      )}
      
      <div className="project-quiz-container d-flex align-items-center" style={{ width: '100%' }}>
        {/* Previous Button - 10% width */}
        <div style={{ flex: '0 0 10%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <button
            onClick={handlePrevious}
            disabled={!(currentContentIndex > 0 || currentSectionIndex > 0)}
            style={{
              fontSize: '3rem',
              width: '80px',
              height: '80px',
              border: 'none',
              background: 'none',
              color: currentContentIndex > 0 || currentSectionIndex > 0 ? 'var(--color-dark)' : '#ccc',
              cursor: currentContentIndex > 0 || currentSectionIndex > 0 ? 'pointer' : 'not-allowed'
            }}
          >
            <i className="bi bi-chevron-left"></i>
          </button>
        </div>

        {/* Content Area - 80% width */}
        <div style={{ flex: '0 0 80%', display: 'flex', justifyContent: 'center', padding: '0 2rem' }}>
          <div style={{ width: '100%' }}>
            <ContentViewer
              currentContent={getCurrentContent()}
              project={groupData.project}
              projectId={projectId}
              onResponseChange={handleResponseChange}
              navigationDirection={navigationDirection}
            />
          </div>
        </div>

        {/* Next Button - 10% width */}
        <div style={{ flex: '0 0 10%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <button
            onClick={handleNext}
            disabled={(() => {
              const currentContent = getCurrentContent();
              const isQuestionContent = currentContent &&
                ['QUESTION_SIMPLE', 'QUESTION_MULTIPLE', 'CHALLENGE', 'IMAGE_COMMENT'].includes((currentContent as any).contentType);
              return (isQuestionContent && !isResponseValid) || isSavingResponse;
            })()}
            style={{
              fontSize: '3rem',
              width: '80px',
              height: '80px',
              border: 'none',
              background: 'none',
              color: 'var(--color-dark)',
              cursor: (() => {
                const currentContent = getCurrentContent();
                const isQuestionContent = currentContent &&
                  ['QUESTION_SIMPLE', 'QUESTION_MULTIPLE', 'CHALLENGE', 'IMAGE_COMMENT'].includes((currentContent as any).contentType);
                return (isQuestionContent && !isResponseValid) || isSavingResponse ? 'not-allowed' : 'pointer';
              })(),
              opacity: (() => {
                const currentContent = getCurrentContent();
                const isQuestionContent = currentContent &&
                  ['QUESTION_SIMPLE', 'QUESTION_MULTIPLE', 'CHALLENGE', 'IMAGE_COMMENT'].includes((currentContent as any).contentType);
                return (isQuestionContent && !isResponseValid) || isSavingResponse ? 0.3 : 1;
              })(),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
{isSavingResponse ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (() => {
              // Check if we're at the last content of the current section
              const currentSection = groupData?.group?.sections?.[currentSectionIndex];
              const isLastContentInSection = currentSection?.contents &&
                currentContentIndex >= currentSection.contents.length - 1;

              return isLastContentInSection ? (
                <i className="bi bi-chevron-double-right"></i>
              ) : (
                <i className="bi bi-chevron-right"></i>
              );
            })()}
          </button>
        </div>
      </div>
    </>
  );
};

export default ProjectQuiz;